// pages/GoogleDocsTranslation.jsx - Main translation workflow with Google Docs
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import GoogleDocsService from '../services/googleDocsService';
import GoogleDocsEditor, { GoogleDocsEditorSideBySide } from '../components/GoogleDocsEditor';
import FullScreenGoogleDocsViewer from '../components/FullScreenGoogleDocsViewer';
import useApiClient from '../services/api';

/**
 * GoogleDocsTranslation - Complete DOCX translation workflow
 * 1. Upload DOCX
 * 2. Translate with Claude (via backend)
 * 3. Create Google Doc from translation
 * 4. Edit in Google Docs
 * 5. Export to DOCX
 */
const GoogleDocsTranslation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { isGoogleAuthenticated, googleLogin, googleLogout, googleUserInfo, googleAccessToken } = useAuth();
  const fileInputRef = useRef(null);

  // Workflow states
  const [step, setStep] = useState(1); // 1: Upload, 2: Translate, 3: Edit, 4: Export
  const [isProcessing, setIsProcessing] = useState(false);

  // Document states
  const [uploadedFile, setUploadedFile] = useState(null);
  const [sourceLanguage, setSourceLanguage] = useState('en');
  const [targetLanguage, setTargetLanguage] = useState('es');
  const [documentType, setDocumentType] = useState('general');

  // Google Docs states
  const [originalDocId, setOriginalDocId] = useState(null);
  const [translatedDocId, setTranslatedDocId] = useState(null);
  const [translatedDocxBlob, setTranslatedDocxBlob] = useState(null);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ar', name: 'Arabic' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' }
  ];

  const documentTypes = [
    { value: 'general', label: 'General Document' },
    { value: 'birth_certificate', label: 'Birth Certificate' },
    { value: 'drivers_license', label: "Driver's License" },
    { value: 'passport', label: 'Passport' },
    { value: 'academic_transcript', label: 'Academic Transcript' },
    { value: 'legal', label: 'Legal Document' },
    { value: 'medical', label: 'Medical Record' }
  ];

  /**
   * Step 1: Handle file upload
   */
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.docx')) {
        toast.error(t('googleDocs.messages.uploadError'));
        return;
      }
      setUploadedFile(file);
      toast.success(t('googleDocs.messages.fileSelectSuccess', { fileName: file.name }));
    }
  };

  /**
   * Step 2: Upload original DOCX to Google Docs
   */
  const handleUploadOriginal = async () => {
    if (!uploadedFile) {
      toast.error(t('googleDocs.messages.selectFileFirst'));
      return;
    }

    if (!isGoogleAuthenticated) {
      toast.error(t('googleDocs.messages.loginRequired'));
      return;
    }

    try {
      setIsProcessing(true);
      const googleDocs = new GoogleDocsService(googleAccessToken);

      // Upload original DOCX to Google Docs
      const { documentId, documentUrl } = await googleDocs.importDocx(
        uploadedFile,
        `Original - ${uploadedFile.name}`
      );

      setOriginalDocId(documentId);
      toast.success(t('googleDocs.messages.uploadSuccess'));
      setStep(2);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t('googleDocs.messages.uploadFailed', { error: error.message }));
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Step 3: Translate with Claude via backend
   */
  const handleTranslate = async () => {
    if (!uploadedFile) {
      toast.error(t('googleDocs.messages.noFileToTranslate'));
      return;
    }

    try {
      setIsProcessing(true);
      toast.info(t('googleDocs.messages.translating'));

      // Send DOCX to backend for translation
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('sourceLanguage', sourceLanguage);
      formData.append('targetLanguage', targetLanguage);
      formData.append('documentType', documentType);

      const response = await apiClient.post('/documents/translate-docx', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Backend returns base64 DOCX - convert to Blob
        const base64Data = response.data.translatedDocx;
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });

        setTranslatedDocxBlob(blob);
        toast.success(t('googleDocs.messages.translateSuccess'));

        // Import translated DOCX into Google Docs
        await importTranslatedDocx(blob);
        setStep(3);
      } else {
        throw new Error(response.data.error || 'Translation failed');
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error(t('googleDocs.messages.translateFailed', { error: error.message }));
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Import translated DOCX into Google Docs
   */
  const importTranslatedDocx = async (docxBlob) => {
    try {
      const googleDocs = new GoogleDocsService(googleAccessToken);

      // Convert Blob to File for import
      const file = new File(
        [docxBlob],
        `translated-${uploadedFile.name}`,
        { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }
      );

      const { documentId, documentUrl } = await googleDocs.importDocx(
        file,
        `Translated - ${uploadedFile.name.replace('.docx', '')} (${sourceLanguage} → ${targetLanguage})`
      );

      setTranslatedDocId(documentId);
      toast.success(t('googleDocs.messages.loadSuccess'));

      // Make document editable by anyone (optional)
      await googleDocs.shareDocument(documentId, null, 'writer');
    } catch (error) {
      console.error('Error importing translated DOCX:', error);
      toast.error(t('googleDocs.messages.loadFailed'));
      throw error;
    }
  };

  /**
   * Step 4: Export translated document to DOCX
   */
  const handleExport = async () => {
    if (!translatedDocId) {
      toast.error(t('googleDocs.messages.noDocToExport'));
      return;
    }

    try {
      setIsProcessing(true);
      toast.info(t('googleDocs.messages.exporting'));

      const googleDocs = new GoogleDocsService(googleAccessToken);
      const blob = await googleDocs.exportToDocx(translatedDocId);

      // Download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `translated-${uploadedFile.name}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('googleDocs.messages.exportSuccess'));
      setStep(4);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('googleDocs.messages.exportFailed', { error: error.message }));
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Reset workflow
   */
  const handleReset = () => {
    setStep(1);
    setUploadedFile(null);
    setOriginalDocId(null);
    setTranslatedDocId(null);
    setTranslatedDocxBlob(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // If in edit mode (step 3), render full-screen viewer
  if (step === 3) {
    return (
      <FullScreenGoogleDocsViewer
        originalDocumentId={originalDocId}
        translatedDocumentId={translatedDocId}
        documentTitle={uploadedFile ? `Translation - ${uploadedFile.name}` : 'Translation'}
        documentType="general"
        saving={isProcessing}
        onExport={handleExport}
        translationId="google-docs-test" // Placeholder for testing - will be replaced with real ID
      />
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Document Translation with Google Docs</h1>
          <p style={styles.subtitle}>
            Upload DOCX → Translate with AI → Edit in Google Docs → Export
          </p>
        </div>

        {/* Google Auth */}
        <div style={styles.authSection}>
          {isGoogleAuthenticated ? (
            <div style={styles.userInfo}>
              <img
                src={googleUserInfo?.picture || 'https://via.placeholder.com/40'}
                alt="Profile"
                style={styles.avatar}
              />
              <div>
                <div style={styles.userName}>{googleUserInfo?.name}</div>
                <button onClick={googleLogout} style={styles.logoutBtn}>
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <button onClick={googleLogin} style={styles.loginBtn}>
              <img
                src="https://www.google.com/favicon.ico"
                alt="Google"
                style={styles.googleIcon}
              />
              Sign in with Google
            </button>
          )}
        </div>
      </div>

      {/* Progress Indicator */}
      <div style={styles.progressBar}>
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            style={{
              ...styles.progressStep,
              ...(step >= s ? styles.progressStepActive : {})
            }}
          >
            {s}
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div style={styles.content}>
        {/* Step 1: Upload */}
        {step === 1 && (
          <div style={styles.step}>
            <h2>Step 1: Upload Document</h2>

            <div style={styles.uploadSection}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                onChange={handleFileSelect}
                style={styles.fileInput}
              />

              {uploadedFile && (
                <div style={styles.fileInfo}>
                  <strong>Selected:</strong> {uploadedFile.name} (
                  {(uploadedFile.size / 1024).toFixed(2)} KB)
                </div>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Source Language:</label>
              <select
                value={sourceLanguage}
                onChange={(e) => setSourceLanguage(e.target.value)}
                style={styles.select}
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Target Language:</label>
              <select
                value={targetLanguage}
                onChange={(e) => setTargetLanguage(e.target.value)}
                style={styles.select}
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Document Type:</label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                style={styles.select}
              >
                {documentTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.actions}>
              <button
                onClick={handleUploadOriginal}
                disabled={!uploadedFile || !isGoogleAuthenticated || isProcessing}
                style={styles.primaryBtn}
              >
                {isProcessing ? 'Uploading...' : 'Upload & Continue'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Translate */}
        {step === 2 && (
          <div style={styles.step}>
            <h2>Step 2: Translate Document</h2>

            <div style={styles.infoBox}>
              <p>
                <strong>Original:</strong> {uploadedFile?.name}
              </p>
              <p>
                <strong>Translation:</strong> {sourceLanguage.toUpperCase()} →{' '}
                {targetLanguage.toUpperCase()}
              </p>
              <p>
                <strong>Document Type:</strong>{' '}
                {documentTypes.find((t) => t.value === documentType)?.label}
              </p>
            </div>

            <div style={styles.actions}>
              <button onClick={handleReset} style={styles.secondaryBtn}>
                Start Over
              </button>
              <button
                onClick={handleTranslate}
                disabled={isProcessing}
                style={styles.primaryBtn}
              >
                {isProcessing ? 'Translating...' : 'Translate with Claude AI'}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 4 && (
          <div style={styles.step}>
            <h2>✅ Translation Complete!</h2>

            <div style={styles.successBox}>
              <p>Your translated document has been exported successfully.</p>
              <p>
                <strong>File:</strong> translated-{uploadedFile?.name}
              </p>
            </div>

            <div style={styles.actions}>
              <button onClick={handleReset} style={styles.primaryBtn}>
                Translate Another Document
              </button>
              <button onClick={() => navigate('/')} style={styles.secondaryBtn}>
                Go to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '24px',
    color: '#1f2937'
  },
  subtitle: {
    margin: 0,
    fontSize: '14px',
    color: '#6b7280'
  },
  authSection: {
    display: 'flex',
    alignItems: 'center'
  },
  loginBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  googleIcon: {
    width: '20px',
    height: '20px'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%'
  },
  userName: {
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '4px'
  },
  logoutBtn: {
    padding: '4px 12px',
    fontSize: '12px',
    backgroundColor: '#f3f4f6',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  progressBar: {
    display: 'flex',
    gap: '10px',
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px'
  },
  progressStep: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
    color: '#9ca3af'
  },
  progressStepActive: {
    backgroundColor: '#3b82f6',
    color: '#fff'
  },
  content: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '30px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  step: {
    maxWidth: '100%'
  },
  uploadSection: {
    marginBottom: '20px'
  },
  fileInput: {
    display: 'block',
    marginBottom: '10px',
    padding: '10px',
    border: '2px dashed #e5e7eb',
    borderRadius: '6px',
    width: '100%'
  },
  fileInfo: {
    padding: '12px',
    backgroundColor: '#F2FBF7',
    border: '1px solid #C7EEDC',
    borderRadius: '6px',
    fontSize: '14px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151'
  },
  select: {
    width: '100%',
    padding: '10px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px'
  },
  infoBox: {
    padding: '20px',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    marginBottom: '20px'
  },
  successBox: {
    padding: '30px',
    backgroundColor: '#F2FBF7',
    border: '2px solid #C7EEDC',
    borderRadius: '8px',
    marginBottom: '20px',
    textAlign: 'center'
  },
  editorContainer: {
    marginBottom: '20px',
    minHeight: '700px'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  },
  primaryBtn: {
    padding: '12px 24px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  secondaryBtn: {
    padding: '12px 24px',
    backgroundColor: '#fff',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

export default GoogleDocsTranslation;
